<script lang="ts" setup>
import { useMyToast } from "@/composable/useMyToast";
import Button from "primevue/button"
import { Form, FormField } from "@primevue/forms";
import SrInputNumber from ".././SrInputNumber.vue";
import { computed, nextTick, ref, watch } from "vue";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import { z } from "zod"
import type { FormSubmitEvent } from "@primevue/forms";
import { i18n } from "@/i18n";
import { isNaN } from "lodash";
import { useTemplateRef } from "vue";
import type { FormInstance } from "@primevue/forms";
import { useAverageSpreadSettings } from "@/composable/settings/useAverageSpreadSettings";
import { srZodNumber } from "@/utils";

const { t } = i18n.global
const form = useTemplateRef<FormInstance>('form')
const { addToast } = useMyToast()
const initValues = { 'spread': null as number | null | undefined }

const root = computed(() => props.root)

const visibleavgSpread = ref<number | null>(null)
const props = defineProps<{
  root: string | null
}>()
const editValue = ref(false)
const { avgSpread, recAvgSpread, autoAvgSpread } = useAverageSpreadSettings(root)
const recAvgSpreadStr = computed(() => recAvgSpread.value ? Number(recAvgSpread.value).toFixed(2) : "")
const triggerResolver = zodResolver(z.object({
  spread: srZodNumber()
}))


const onFormSubmit = (e: FormSubmitEvent) => {
  if (!props.root) {
    return
  }
  if (!e.valid) return
  const tried = Number(e.values.spread)
  if (e.values.spread && isNaN(tried)) {
    editValue.value = true
    addToast({
      severity: "error",
      message: t('modal.createSwapOrders.errors.invalidAvgSpreadFormat')
    })
    return
  }
  let msg = 'modal.createSwapOrders.avgSpreadSuccessfullySet'
  let newValue = e.values.spread ?? null
  if (newValue === '') {
    newValue = null
  }
  if (newValue !== null && newValue !== undefined) {
    avgSpread.value = newValue
  } else {
    avgSpread.value = null
    msg = 'modal.createSwapOrders.avgSpreadSuccessfullyUnset'
  }
  editValue.value = false
  avgSpread.value = newValue
  addToast({
    severity: "success",
    message: t(msg)
  })
}

watch(editValue, async (v) => {
  if (v) {
    await nextTick()
    form.value?.setFieldValue('spread', visibleavgSpread.value ?? avgSpread.value)
    if (visibleavgSpread.value) {
      visibleavgSpread.value = null
    }
  }
})

defineExpose({ visibleavgSpread })
</script>

<template>
  <div class="grid grid-cols-4 col-span-1">

    <Button fluid @click="autoAvgSpread = !autoAvgSpread" class="rounded-r-none border-r-0" severity="success"
      variant="outlined">
      <i class="pi" :class="{ 'pi-check-square': autoAvgSpread, 'pi-stop': !autoAvgSpread }"></i>
      <span>{{ $t("modal.createSwapOrders.auto") }}</span>
    </Button>

    <div class="grid grid-cols-2 col-span-3">
      <Button fluid class="col-span-2 rounded-l-none" v-if="!Boolean(editValue)"
        :variant="avgSpread === null ? 'outlined' : undefined" severity="success" @click="editValue = true">
        <span v-if="avgSpread === null">{{ $t("modal.createSwapOrders.avgSpread") }}</span>
        <span v-else>{{ $t("modal.createSwapOrders.avgSpread") }}: {{ Number(avgSpread).toFixed(2) }}%</span>
      </Button>
      <div v-else class="col-span-2 rounded-l-none">
        <Form ref="form" :resolver="triggerResolver" @submit="onFormSubmit" :initial-values="initValues">
          <FormField name="spread" class="grid grid-cols-2 relative">
            <Button class="rounded-r-none rounded-l-none" type="submit" severity="success" variant="outlined">
              <span class="hidden md:inline">{{ $t("modal.createSwapOrders.avgSpread") }}</span>
              <span class="md:hidden">{{ $t("modal.createSwapOrders.avgSpread") }}</span>
            </Button>
            <SrInputNumber class="rounded-l-none border-l-0" :placeholder="recAvgSpreadStr" fluid variant="outlined"
              :step="0.1" :min="-200" :max="200" :maxFractionDigits="2">
            </SrInputNumber>
          </FormField>
        </Form>
      </div>
    </div>
  </div>
</template>
